import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  AutomationIcon,
  BulkTaskIcon,
  ClickAwayListener,
  DeleteIcon,
  DownIcon,
  Edit1Icon,
  GKButton,
  GKDataGrid,
  Grid2,
  IconButton,
  NoData,
  PlusIcon,
  PopConfirm,
  Popover,
  Show,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { AutomationList } from "interfaces/Revenue";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBulkDeleteAutomationMutation,
  useDeleteSingleAutomationMutation,
  useGetAutomationListQuery,
} from "store/features/Revenue/ProformaApi";
import { extraColumnsField, matchDate } from "utils/helpers";
import SingleAutomationModal from "./SingleAutomationModal";

const Automation = () => {
  const navigate = useNavigate();

  const [openSingleAutomationModal, setOpenSingleAutomationModal] =
    useState(false);
  const [automationData, setAutomationData] = useState(null);
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const open = Boolean(anchorEl);
  const popoverId = open ? "simple-popover" : undefined;

  const requestBody = useGenerateQueryParams("automation");

  const { data, isFetching } = useGetAutomationListQuery(requestBody);

  const [deleteSingleAutomation, { isLoading }] =
    useDeleteSingleAutomationMutation();
  const [bulkDeleteAutomation, { isLoading: bulkDeleteLoading }] =
    useBulkDeleteAutomationMutation();

  const handleDeleteAutomation = (
    event: React.MouseEvent<HTMLButtonElement>,
    params: GridRenderCellParams<AutomationList>,
  ) => {
    event.stopPropagation();
    setAutomationData(params?.row);
    setConfirmationModal(event.currentTarget);
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio (${data?.totalCount || 0})`,
        "clientName",
        250,
        1,
      ),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography
              fontWeight={700}
              maxWidth={"100%"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    { ...extraColumnsField("Fee Plan Name", "feePlanName", 170, 1) },
    {
      ...extraColumnsField("Start Date", "startingDate", 100, 1),
      type: "date",
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField(
        "Next Mgt. Fees Date",
        "managementNextPaymentDate",
        170,
        1,
      ),
      type: "date",
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField(
        "Next Perf. Fees Date",
        "performanceNextPaymentDate",
        170,
        1,
      ),
      type: "date",
      valueFormatter: (value) => matchDate(value?.value),
    },
    {
      ...extraColumnsField("Actions", "action", 100, 1),
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="error"
          disabled={
            (isLoading && params?.row?.id === automationData?.id) ||
            bulkDeleteLoading
          }
          onClick={(event) => handleDeleteAutomation(event, params)}
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const handleOpenBulkAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenNewAutomation = () => {
    setAutomationData(null);
    setOpenSingleAutomationModal(true);
  };

  const handleNavigate = (path: string) => navigate(path);

  return (
    <>
      <GKDataGrid
        loading={isFetching || bulkDeleteLoading}
        columns={columns}
        rows={data?.data || []}
        checkboxSelection
        onRowSelectionModelChange={(params) => setSelectedIds(params)}
        rowSelectionModel={selectedIds}
        maxHeight={"calc(100vh - 312px)"}
        totalCount={data?.totalCount || 0}
        name="automation"
        tableName="automation"
        onRowClick={(params) => {
          setAutomationData(params.row);
          setOpenSingleAutomationModal(true);
        }}
        headerComponent={
          <Grid2 container spacing={1}>
            {selectedIds?.length !== 0 && (
              <GKButton
                variant="outlined"
                size="medium"
                color="error"
                onClick={(event) => {
                  setConfirmationModal(event.currentTarget);
                }}
              >
                Delete
              </GKButton>
            )}
            {selectedIds?.length !== 0 && (
              <GKButton
                variant="outlined"
                size="medium"
                color="primary"
                onClick={() => {
                  setSelectedIds([]);
                }}
              >
                Cancel
              </GKButton>
            )}
            <Show.When isTrue={selectedIds?.length === 0}>
              <GKButton
                variant="outlined"
                color="dark"
                endIcon={
                  <DownIcon
                    style={{
                      transform: open ? "rotate(0.5turn)" : "rotate(0turn)",
                      transition: "all 0.3s",
                    }}
                  />
                }
                onClick={handleOpenBulkAction}
              >
                Bulk Action
              </GKButton>
              <GKButton
                variant="contained"
                startIcon={<PlusIcon width={16} height={16} />}
                onClick={handleOpenNewAutomation}
              >
                New Automation
              </GKButton>
            </Show.When>
          </Grid2>
        }
        emptyComponent={
          <NoData
            text="Ready to Automate?"
            subText="Build smart workflows that handle the routine, So you can focus on strategy."
            customImg={
              <AutomationIcon
                height={80}
                width={80}
                color="var(--neutral-100)"
              />
            }
            additionalComponent={
              <Grid2 container spacing={1}>
                <GKButton
                  variant="outlined"
                  color="dark"
                  startIcon={<BulkTaskIcon width={16} height={16} />}
                  onClick={() =>
                    handleNavigate(
                      "/app/revenue/automation/bulk-automation/create",
                    )
                  }
                >
                  Add Bulk Action
                </GKButton>
                <GKButton
                  variant="contained"
                  startIcon={<PlusIcon width={16} height={16} />}
                  onClick={handleOpenNewAutomation}
                >
                  New Automation
                </GKButton>
              </Grid2>
            }
          />
        }
      />

      {Boolean(anchorEl) && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Popover
            id={popoverId}
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  width: "180px",
                  borderRadius: "8px",
                  p: "8px 0",
                  border: "1px solid var(--neutral-100)",
                  bgcolor: "var(--white)",
                  boxShadow: "4px 4px 12px 0px rgba(0, 0, 0, 0.25);",
                },
              },
            }}
          >
            <Grid2 container>
              <Grid2
                size={12}
                p={"12px"}
                display={"flex"}
                alignItems={"center"}
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  handleNavigate(
                    "/app/revenue/automation/bulk-automation/create",
                  )
                }
              >
                <BulkTaskIcon />
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="var(--grey-900)"
                >
                  Add Bulk Automation
                </Typography>
              </Grid2>
              <Grid2
                size={12}
                p={"12px"}
                display={"flex"}
                alignItems={"center"}
                gap={1}
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  navigate("/app/revenue/automation/bulk-automation/update");
                }}
              >
                <Edit1Icon />
                <Typography
                  fontSize={12}
                  fontWeight={600}
                  color="var(--grey-900)"
                >
                  Edit Bulk Automation
                </Typography>
              </Grid2>
            </Grid2>
          </Popover>
        </ClickAwayListener>
      )}

      <Show.When isTrue={openSingleAutomationModal}>
        <SingleAutomationModal
          open={openSingleAutomationModal}
          setOpen={setOpenSingleAutomationModal}
          automationData={automationData}
        />
      </Show.When>

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={confirmationModal}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          onSuccess={async () => {
            try {
              if (selectedIds?.length === 0) {
                await deleteSingleAutomation(automationData?.id).unwrap();
              } else {
                await bulkDeleteAutomation({ ids: selectedIds }).unwrap();
                setConfirmationModal(null);
                setSelectedIds([]);
              }
            } catch (err) {
              console.error("Delete automation failed:", err);
            }
          }}
          modalTitle="Delete Portfolio Automation?"
          message="Are you sure you want to delete the automation settings for this portfolio?"
        />
      </Show.When>
    </>
  );
};

export default Automation;
